import AppConfig from '../appConfig';

/**
 * Class used to communicate with the backend authentication system
 * This class currently uses a mock authentication system
 */
class LoginFetch {
  /**
   * Generic error handler for Fetch calls, throws an error if the Fetch call returned anything other than a 200
   *
   * @param response
   * @returns {*}
   */
  static handleErrors(response) {
    if (response.ok) {
      return response;
    }
    throw Error(response.status);
  }

  /**
   * Calls the login API function, the Promise should resolve to a json.jwt string with the token
   *
   * @param email
   * @param password
   * @returns {Promise.<*>}
   */
  static login(email, password) {
    var form = new FormData();
    form.append('username', email);
    form.append('password', password);

    return fetch(AppConfig.loginUrl, {
      credentials: 'include',
      method: 'POST',
      body: form,
      cache: 'no-cache',
    })
      .then(LoginFetch.handleErrors)
      .then((response) => response.json());
  }

  /**
   * Calls the find user info function, which verifies the token is valid. The Promise should resolve to a json
   * string containing all user info, entitlements
   *
   * For Zywave users, also fetches current profile from Zywave API to ensure profile data is up-to-date
   * after profile switches
   *
   * @returns {Promise.<*>}
   */
  static async findUserInfo() {
    // Add timestamp to URL to prevent browser/CDN caching (cache-busting)
    const cacheBustUrl = `${AppConfig.findUserInfoUrl}?_t=${Date.now()}`;

    // Fetch user info from miEdge backend
    const userInfo = await fetch(cacheBustUrl, {
      credentials: 'include',
      method: 'GET',
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
    })
      .then(LoginFetch.handleErrors)
      .then((response) => response.json());

    // For Zywave users (account_type === 7), fetch current profile from Zywave API
    // to ensure it's up-to-date after profile switches
    if (userInfo.account_type === 7 && AppConfig.zywaveProxyUrl) {
      try {
        await customElements.whenDefined('zywave-api-proxy');

        // Fetch current profile from Zywave
        const profileResponse = await fetch(
          `${AppConfig.zywaveProxyUrl}profiles/V2.0/Profile/B/current?_t=${Date.now()}`,
          {
            credentials: 'include',
            method: 'GET',
            cache: 'no-store',
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
            },
          }
        );

        if (profileResponse.ok) {
          const profileData = await profileResponse.json();

          // Update profile-specific fields with fresh data from Zywave
          userInfo.zywave_profile_id = profileData.id;
          userInfo.zywave_profile_type = profileData.typeCode;

          // Extract agency ID and organization IDs from claims
          if (profileData.claims && Array.isArray(profileData.claims)) {
            const brokerIdClaim = profileData.claims.find(
              (c) => c.type === 'urn:zywave:profile:b:brokerid'
            );
            if (brokerIdClaim) {
              userInfo.zywave_agency_id = parseInt(brokerIdClaim.value);
            }

            const orgIdClaim = profileData.claims.find(
              (c) => c.type === 'urn:zywave:profile:b:organizationid'
            );
            if (orgIdClaim) {
              userInfo.zywave_organization_id = orgIdClaim.value;
            }

            const orgUnitIdClaim = profileData.claims.find(
              (c) => c.type === 'urn:zywave:profile:b:organizationalunitid'
            );
            if (orgUnitIdClaim) {
              userInfo.zywave_organization_unit_id = orgUnitIdClaim.value;
            }
          }
        }
      } catch (error) {
        // Silently fail - use cached data from backend if Zywave API call fails
        console.warn('Error fetching Zywave profile data, using cached data:', error);
      }
    }

    return userInfo;
  }

  /**
   * Logout user
   *
   * @returns {Promise.<*>}
   */
  static logout() {
    return fetch(AppConfig.apiUrl + '/v1/auth/logout', {
      credentials: 'include',
      method: 'GET',
      cache: 'no-cache',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
      },
    }).then((response) => {
      if (!response.ok) {
        console.log(
          'ERROR, but resume next. Status: ' + response.status + ' ' + response.statusText
        );
        throw Error(response.status);
      }
      return response.json();
    });
  }

  /**
   * Calls the forgot password API function
   *
   * @param current_password
   * @param password
   * @param password_confirm
   * @returns {Promise.<*>}
   */
  static forgotPassword(lost_username, lost_password, username, email) {
    var form = new FormData();
    form.append('lost_username', lost_username);
    form.append('lost_password', lost_password);
    form.append('username', username);
    form.append('email', email);

    return fetch(AppConfig.apiUrl + '/v1/site/forgotpassword', {
      credentials: 'include',
      method: 'POST',
      body: form,
      cache: 'no-cache',
    })
      .then(LoginFetch.handleErrors)
      .then((response) => response.json());
  }

  static checkAccessToken() {
    return fetch(AppConfig.apiUrl + '/v1/auth/ping', {
      credentials: 'include',
      method: 'GET',
      cache: 'no-cache',
    })
      .then((response) => {
        return response.json();
      })
      .then((json) => {
        if (json.status && json.status !== 200) {
          throw Error(json.status);
        }
        return json;
      });
  }
}

export default LoginFetch;
